import { GQL } from '@aline/graphql';

/**
 * Statuses shown on rules and rolled up to categories and the playbook.
 * `failed` exists only at rule level and means the run itself errored, as
 * opposed to a rule whose positions were evaluated and failed. `reviewing` is
 * `review` with work underway: someone recorded a decision and the rule has
 * not resolved yet.
 */
export type PlaybookRuleStatus =
  | 'failed'
  | 'not-run'
  | 'passed'
  | 'resolved'
  | 'review'
  | 'reviewing'
  | 'running';

/**
 * The playbook rollup. There is deliberately no `failed` here: a run that
 * errored still leaves the playbook needing a person, and a playbook whose
 * reviewable rules are all resolved reads as Resolved even though that status
 * is derived, never stored.
 */
export type PlaybookStatus = Exclude<PlaybookRuleStatus, 'failed'>;

/**
 * One rule's run against a document, reduced to what deriving a status needs.
 * Both readers build it — the client from the run query's rule job, the server
 * from the job row and its proposals — so a list card's chip and the drawer's
 * rollup are the same derivation over the same facts and cannot disagree.
 *
 * Null stands for a rule that has never been run against the document.
 */
export interface PlaybookRuleRun {
  /** When the run gave up, if it did. */
  failedAt: Date | string | null;
  /** When the run finished, if it did. */
  finishedAt: Date | string | null;
  /** Whether any evaluated position failed against the document. */
  hasFailingPosition: boolean;
  /** Whether a decision is recorded against any of the run's proposals. */
  hasReviewDecision: boolean;
  /** Whether the reviewer settled the rule, whichever action they took. */
  isResolved: boolean;
  /** When the run started, if it has. */
  startedAt: Date | string | null;
}

export function getIsPlaybookRuleRunning(run: PlaybookRuleRun | null): boolean {
  return Boolean(run?.startedAt && !run.finishedAt && !run.failedAt);
}

export function getIsPlaybookRuleRunFinished(run: PlaybookRuleRun | null): boolean {
  return Boolean(run?.finishedAt);
}

/**
 * Whether the run itself errored, as opposed to a rule whose positions were
 * evaluated and failed.
 *
 * A recorded finish outranks a failure stamp, because a re-run clears both
 * before it starts: a job carrying the two at once is one that was stamped by
 * an attempt that gave up — a whole run marked failed because its workflow
 * start errored, say — and then finished anyway. Reading `failedAt` alone
 * reports a rule with a full set of results as having failed to run.
 */
export function getIsPlaybookRuleRunFailed(run: PlaybookRuleRun | null): boolean {
  return Boolean(run?.failedAt) && !run?.finishedAt;
}

/**
 * A reviewable rule: run, with at least one failed position. Rules that pass
 * at the starting position never participate in the resolution workflow.
 */
export function getIsPlaybookRuleReviewable(run: PlaybookRuleRun | null): boolean {
  return getIsPlaybookRuleRunFinished(run) && Boolean(run?.hasFailingPosition);
}

/**
 * Review underway: a decision is recorded and the rule has not resolved.
 * Decisions travel on every run, so this reads true down to the playbook list,
 * including for a collaborator's review.
 */
export function getIsPlaybookRuleMidReview(run: PlaybookRuleRun | null): boolean {
  if (!run || run.isResolved) return false;
  return run.hasReviewDecision;
}

export function getPlaybookRuleStatusForRun(run: PlaybookRuleRun | null): PlaybookRuleStatus {
  if (getIsPlaybookRuleRunning(run)) return 'running';
  if (getIsPlaybookRuleRunFailed(run)) return 'failed';
  if (!getIsPlaybookRuleRunFinished(run)) return 'not-run';
  if (run?.isResolved) return 'resolved';
  if (!getIsPlaybookRuleReviewable(run)) return 'passed';
  return getIsPlaybookRuleMidReview(run) ? 'reviewing' : 'review';
}

/**
 * The playbook rollup over one run per rule, in any order. Pass a null for
 * every rule the document has no run for: a playbook is only Passed or
 * Resolved once every one of its rules has an outcome.
 */
export function getPlaybookStatusForRuns(
  runs: readonly (PlaybookRuleRun | null)[]
): PlaybookStatus {
  let allRun = runs.length > 0;
  let hasOutcome = false;
  let hasResolved = false;
  let hasReview = false;
  let hasReviewing = false;

  for (const run of runs) {
    switch (getPlaybookRuleStatusForRun(run)) {
      case 'running':
        return 'running';
      case 'failed':
      case 'review':
        hasOutcome = true;
        hasReview = true;
        break;
      case 'reviewing':
        hasOutcome = true;
        hasReviewing = true;
        break;
      case 'resolved':
        hasOutcome = true;
        hasResolved = true;
        break;
      case 'passed':
        hasOutcome = true;
        break;
      case 'not-run':
        allRun = false;
        break;
    }
  }

  if (!hasOutcome) return 'not-run';
  // Work underway outranks work waiting: Reviewing before Review.
  if (hasReviewing) return 'reviewing';
  if (hasReview || !allRun) return 'review';
  if (hasResolved) return 'resolved';
  return 'passed';
}

/**
 * The wire enum for a rollup the server derived. The two maps are inverses of
 * each other — a test pins that — and exist because the API speaks the
 * generated enum while the UI keys its chips, tones and filters off the
 * lowercase union.
 */
export const PLAYBOOK_RUN_STATUS_BY_PLAYBOOK_STATUS = {
  'not-run': GQL.PlaybookRunStatus.NotRun,
  passed: GQL.PlaybookRunStatus.Passed,
  resolved: GQL.PlaybookRunStatus.Resolved,
  review: GQL.PlaybookRunStatus.Review,
  reviewing: GQL.PlaybookRunStatus.Reviewing,
  running: GQL.PlaybookRunStatus.Running,
} as const satisfies Record<PlaybookStatus, GQL.PlaybookRunStatus>;

export const PLAYBOOK_STATUS_BY_RUN_STATUS = {
  [GQL.PlaybookRunStatus.NotRun]: 'not-run',
  [GQL.PlaybookRunStatus.Passed]: 'passed',
  [GQL.PlaybookRunStatus.Resolved]: 'resolved',
  [GQL.PlaybookRunStatus.Review]: 'review',
  [GQL.PlaybookRunStatus.Reviewing]: 'reviewing',
  [GQL.PlaybookRunStatus.Running]: 'running',
} as const satisfies Record<GQL.PlaybookRunStatus, PlaybookStatus>;
